import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { inputClass } from "@/lib/formStyles";
import ModalUI from "../legacy/Modal";
import VisitReasonBadge from "./VisitReasonBadge";
import {
  VISIT_REASON_COLORS,
  VISIT_REASON_LABEL_MAX_LENGTH,
  VisitReasonApi,
  VisitReasonColor,
  VisitReasonPayload,
  colorStyle,
} from "../../services/visitReasons/types";

type VisitReasonFormModalProps = {
  isOpen: boolean;
  /** Motif modifié ; absent = création. */
  visitReason?: VisitReasonApi | null;
  isSaving: boolean;
  onSubmit: (payload: VisitReasonPayload) => void;
  onClose: () => void;
};

/**
 * Création / modification d'un motif de visite : libellé, couleur, ordre
 * d'affichage et activation. En création, l'ordre vide place le motif en fin de
 * liste (règle back). Remonter le composant (prop `key`) pour le réinitialiser.
 */
const VisitReasonFormModal: React.FC<VisitReasonFormModalProps> = ({
  isOpen,
  visitReason,
  isSaving,
  onSubmit,
  onClose,
}) => {
  const isEdition = visitReason != null;
  const [label, setLabel] = useState(visitReason?.label ?? "");
  const [color, setColor] = useState<VisitReasonColor>(visitReason?.color ?? "BLUE");
  const [displayOrder, setDisplayOrder] = useState(
    visitReason != null ? String(visitReason.displayOrder) : ""
  );
  const [activated, setActivated] = useState(visitReason?.activated ?? true);

  const canSubmit = label.trim().length > 0 && !isSaving;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      label: label.trim(),
      color,
      displayOrder: displayOrder === "" ? undefined : Number(displayOrder),
      activated,
    });
  };

  return (
    <ModalUI
      isOpen={isOpen}
      onClose={() => {
        if (!isSaving) onClose();
      }}
      styles={{ width: "min(520px, 92vw)", padding: 0 }}
    >
      <form onSubmit={submit} className="flex flex-col p-6 space-y-4">
        <h3 className="text-lg font-semibold text-slate-800">
          {isEdition ? "Modifier le motif" : "Nouveau motif de visite"}
        </h3>

        <label className="block text-sm font-medium text-slate-700">
          Libellé
          <input
            type="text"
            className={inputClass}
            value={label}
            maxLength={VISIT_REASON_LABEL_MAX_LENGTH}
            placeholder="Ex. Contrôle / suivi"
            onChange={(event) => setLabel(event.target.value)}
            autoFocus
          />
        </label>

        <div>
          <span className="block text-sm font-medium text-slate-700 mb-2">Couleur</span>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Couleur">
            {VISIT_REASON_COLORS.map((colorOption) => (
              <button
                key={colorOption}
                type="button"
                role="radio"
                aria-checked={color === colorOption}
                aria-label={colorStyle(colorOption).name}
                title={colorStyle(colorOption).name}
                onClick={() => setColor(colorOption)}
                className={cn(
                  "h-8 w-8 rounded-full ring-offset-2 transition-all duration-200",
                  colorStyle(colorOption).dot,
                  color === colorOption ? "ring-2 ring-slate-700" : "hover:scale-110"
                )}
              />
            ))}
          </div>
        </div>

        <div className="flex items-end gap-4">
          <label className="block w-40 text-sm font-medium text-slate-700">
            Ordre d'affichage
            <input
              type="number"
              min={0}
              className={inputClass}
              value={displayOrder}
              placeholder="En fin de liste"
              onChange={(event) => setDisplayOrder(event.target.value)}
            />
          </label>
          <label className="flex h-11 items-center gap-2 text-sm font-medium text-slate-700">
            <input
              type="checkbox"
              checked={activated}
              onChange={(event) => setActivated(event.target.checked)}
              className="h-4 w-4 accent-emerald-600"
            />
            Proposé à l'accueil
          </label>
        </div>

        <div className="flex items-center gap-2 text-sm text-slate-500">
          Aperçu : <VisitReasonBadge label={label.trim() || "Libellé"} color={color} />
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="h-10 px-4 rounded-md border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
          >
            {isEdition ? "Enregistrer" : "Ajouter le motif"}
          </button>
        </div>
      </form>
    </ModalUI>
  );
};

export default VisitReasonFormModal;
